//@ts-nocheck
import { Tag } from "antd";
import User from "components/User";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import useAPIResponse from "hooks/useAPIResponse";
import _ from "lodash";
import React, { useMemo } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { NameIdPair } from "types/api/nameIdPair";
import { RiskRegisterListingResponseDto } from "types/api/riskRegisterListingResponseDto";
import { RiskAssessmentTypes } from "types/tenantConfig";
import { parseDateToDisplay } from "utils/dateUtils";
import { CellRenderers, getFilterProps } from "utils/tableUtils";

import ACDateRangeColumnFilter from "ac-components/ACDateRangeColumnFilter";
import ACResolvedUnresolvedFindingsBar from "ac-components/ACResolvedUnresolvedFindingsBar/ACResolvedUnresolvedFindingsBar";
import { ACTableColumnType } from "ac-components/ACTable/ACTable";

import ExceptionIdCell from "./ExceptionDetails/ModalComponents/ExceptionIDCell";

export default function useExceptionColumns({
  showExceptionDetail,
}: { showExceptionDetail?: (id: string, name: string) => void } = {}) {
  const location = useLocation();
  const { url } = useRouteMatch();
  const { id: userId } = useActiveUserContext();

  const [exceptionConfig, loadingExceptionConfig] = useAPIResponse<any>(
    () => GlobalSettingAPI.getExceptionConfig(),
    [],
  );

  const [riskAssessmentFields, loadingRiskAssessmentFields] =
    useAPIResponse<any>(
      () => GlobalSettingAPI.getExceptionRiskAssessmentFields(),
      [],
    );

  const allColumns = useMemo(() => {
    const columns: ACTableColumnType<RiskRegisterListingResponseDto>[] = [
      {
        title: "ID",
        dataIndex: "id",
        width: 100,
        key: "id",
        render: (value, record) => (
          <ExceptionIdCell
            id={value}
            url={url}
            search={location.search}
            name={record.name}
            showExceptionDetail={showExceptionDetail}
          />
        ),
        showSort: true,
        alwaysDisplay: true,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 180,
        ellipsis: true,
        render: CellRenderers.VALUE_OR_NA,
        showSort: true,
        optional: true,
        default: true,
      },
      {
        title: "Reasons",
        dataIndex: "reasons",
        key: "reasons",
        width: 180,
        showSort: true,
        showFilter: true,
        render: (values) =>
          CellRenderers.PLUS_MORE_SINGLE_LINE({ values, className: "" }),
        optional: true,
        default: true,
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        width: 180,
        showSort: true,
        showFilter: true,
        optional: true,
        default: true,
        render: CellRenderers.DATE,
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        width: 180,
        showSort: true,
        showFilter: true,
        optional: true,
        default: true,
        render: (endDate, record) =>
          record.prevEndDate ? (
            <>
              {CellRenderers.DATE(endDate)}{" "}
              <del>{CellRenderers.DATE(record.prevEndDate)}</del>
            </>
          ) : (
            CellRenderers.DATE(endDate)
          ),
      },
      {
        title: "Submitted By",
        dataIndex: "createdBy",
        key: "createdBy",
        width: 300,
        showSort: true,
        showFilter: true,
        showNameInFilter: true,
        optional: true,
        default: true,
        render: (createdBy) => <User userEmail={createdBy} />,
      },
      {
        title: "Approved On",
        dataIndex: "approvedOn",
        key: "approvedOn",
        width: 130,
        showSort: true,
        showFilter: true,
        optional: true,
        default: true,
        render: CellRenderers.DATE,
      },
      {
        title: "Submitted On",
        dataIndex: "submittedOn",
        key: "submittedOn",
        width: 130,
        showSort: true,
        showFilter: true,
        optional: true,
        default: true,
        render: CellRenderers.DATE,
      },
      {
        title: "Findings resolved %",
        dataIndex: "findingCount",
        width: 200,
        render: (value, record) => (
          <ACResolvedUnresolvedFindingsBar
            findingStatusCountMap={record.findingStatusCountMap}
            payload={{ filters: { riskRegister: [record.id] } }}
          />
        ),
        optional: true,
        default: true,
      },
      {
        title: "Scope",
        dataIndex: "scope",
        width: 300,
        optional: true,
        default: true,
        render: (scope) => {
          if (_.isEmpty(scope)) {
            return CellRenderers.VALUE_OR_NA();
          }
          const { product, subProduct, environmentName } = scope || {};
          const subProductDetails =
            subProduct?.map((sub: NameIdPair) => sub.name) || [];
          let formattedScope = product?.name || CellRenderers.VALUE_OR_NA();

          if (subProductDetails.length > 0) {
            formattedScope += `: ${subProductDetails.join(", ")}`;
          }
          if (environmentName) {
            formattedScope += `, ${CellRenderers.VOCABULARY_RENDERER(environmentName)}`;
          }
          return <span>{formattedScope}</span>;
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filterIndex: 1,
        width: 300,
        optional: true,
        default: true,
        showSort: true,
        showFilter: true,
        render: (status, record) => {
          const extended = record.extended ? (
            <Tag className="info">Extended</Tag>
          ) : (
            ""
          );
          if (status === "Waiting") {
            const approvalInfo = record.nextReviewer;
            const isRole = approvalInfo?.type === "ROLE";
            const displayName = approvalInfo
              ? (userId as unknown as number) === approvalInfo.id
                ? "You"
                : approvalInfo.type === "EXTERNAL_USER"
                  ? approvalInfo.email
                  : `${approvalInfo.displayName}${isRole ? "(Role)" : ""}`
              : null;
            return (
              <div style={{ display: "flex", gap: 8, whiteSpace: "nowrap" }}>
                {CellRenderers.TAGS(status)} {displayName} {extended}
              </div>
            );
          } else {
            return (
              <div style={{ display: "flex", gap: 8, whiteSpace: "nowrap" }}>
                {CellRenderers.TAGS(status)} {extended}
              </div>
            );
          }
        },
      },
      {
        title: "Approve By Date",
        dataIndex: "approveByDate",
        key: "approveByDate",
        width: 300,
        optional: true,
        default: true,
        showSort: true,
        render: CellRenderers.DATE,
        showFilter: true,
      },
    ];

    if (
      exceptionConfig?.riskAssessmentEnabled &&
      !_.isEmpty(riskAssessmentFields?.riskAssessmentFields)
    ) {
      return [
        ...columns,
        ..._.map(
          riskAssessmentFields.riskAssessmentFields,
          (riskAssessmentField) => ({
            title: riskAssessmentField.label,
            dataIndex: `riskAssessmentFields.${riskAssessmentField.id}`,
            key: `riskAssessmentFields.${riskAssessmentField.id}`,
            width: 300,
            showSort: true,
            showFilter: true,
            optional: true,
            default: false,
            riskAssessmentFieldType: riskAssessmentField.type,
            render: (value, record) => {
              const isText =
                [
                  RiskAssessmentTypes["Text-Long"],
                  RiskAssessmentTypes["Text-Short"],
                ].indexOf(riskAssessmentField.type) !== -1;
              const isDate =
                RiskAssessmentTypes["Date"] === riskAssessmentField.type;
              const { values } =
                _.find(record.riskAssessmentFields, {
                  id: riskAssessmentField.id,
                }) || {};

              if (isDate) {
                return CellRenderers.DATE(values?.[0]);
              }
              if (!isText && _.isArray(values)) {
                return CellRenderers.PLUS_MORE_SINGLE_LINE({ values });
              }
              return CellRenderers.VALUE_OR_NA(values?.[0]);
            },
            ...(RiskAssessmentTypes["Date"] === riskAssessmentField.type
              ? {
                  filterDropdown: (props) => (
                    <ACDateRangeColumnFilter {...props} />
                  ),
                  filterLabelFormatter: parseDateToDisplay,
                }
              : getFilterProps({
                  nullFilterValue: null,
                  onlySearch:
                    [
                      RiskAssessmentTypes["Text-Long"],
                      RiskAssessmentTypes["Text-Short"],
                    ].indexOf(riskAssessmentField.type) !== -1,
                  filters: riskAssessmentField.values,
                  filterLabelFormatter: (value) =>
                    value !== null ? value : CellRenderers.VALUE_OR_NA(),
                })),
          }),
        ),
      ];
    }
    return columns;
  }, [
    exceptionConfig?.riskAssessmentEnabled,
    riskAssessmentFields?.riskAssessmentFields,
    url,
    location.search,
    showExceptionDetail,
    userId,
  ]);

  return loadingRiskAssessmentFields || loadingExceptionConfig
    ? []
    : allColumns;
}
